import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import _, { forEach, has, isEmpty, map, mapValues, startsWith } from 'lodash';
import UIUtils from 'js/lib/UIUtils';
import { ApiConfig, Credential, Field, SourceConfig, TemplatizedAPIConfig } from '@nexla/@types';
import usePaging from 'new/state/paging/repository/usePaging';
import { useDataMaps } from 'new/hooks/adapters/useDataMaps';
import useFlowsSetup from 'new/components/flows/controller/useFlowsSetup';
import { useGetMetaInfoByConnectorType } from 'new/hooks/useGetMetaInfoByConnectorType';
import { probeRemoveRestStage } from 'js/actions/probe';
import { useDispatch } from 'react-redux';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { PROBE_GET_SAMPLE_REQUEST } from 'js/constants/actionTypes';

import { HC_SOURCE_FIELD_NAMES_AND_OTHERS } from 'new/components/flows/configure/RestAPISource/RestAPISource.constants';
import { useCurrentOrg } from 'new/hooks/adapters/useCurrentOrg';
import useProbe from 'new/state/probe/repository/useProbe';
import useRequests from 'new/state/request/repository/useRequests';
import { RestTemplatizedAPI } from 'new/components/flows/configure/RestAPISource/RestTemplatizedAPI';
import { CONNECTION_CATEGORY_TEMPLATIZED_API } from 'js/constants/connectionCategories';
import useProbeAdapter from 'new/state/probe/adapter/useProbeAdapter';
import {
  filterFieldsBasedOnHCSourceField,
  getFormDataFromCredential,
  getIterationKey,
  getNonDataMapIterations,
  handleAPIResponseStage,
  RestAPIUtil,
  setVendorInformation,
} from './RestAPISource.utils';
import { RestAPISource } from 'new/components/flows/configure/RestAPISource/RestAPISource';
import { getActiveItems } from 'new/components/flows/types/DirectFlow/utils';
import { CenteredLoader } from 'new/elements/CenteredLoader';
import { tryCatch } from 'new/utils/TryCatch';
import useCredentials from 'new/state/credentials/repository/useCredentials';
import { useHistory } from 'react-router-dom';
import useFlowParams from '../../useFlowParams';
import { UiMode } from 'new/constants/uiMode';

type Props = {
  formType: string;
  flowType: string;
  selectedConnection: string;
  selectedCredential: any;
  apiConfig: any;
  sourceConfig: SourceConfig;
  onSourceConfigChanged(data: any): void;
  onSaveAdvancedMode(data: any): void;
  nextFormStage(...args: any): void;
  onError(msg: string): void;
};

type ContextValue = [
  {
    meta: any;
    paging: any;
    formType: string;
    flowType: string;
    apiConfig: ApiConfig;
    sourceConfig: SourceConfig;
    selectedConnection: string;
    selectedCredential: Credential;
    templatizedAPIConfig: TemplatizedAPIConfig;
    isLoadingDataMaps: boolean;
    advancedFields: Field[];
    visibleFields: Field[];
    apiResponseByStage: any;
    apiResponseUpdateTime: any;
    isLoadingProbeSample: boolean;
    dataPathValidationResultByStage: any;
  },
  {
    showError(...args: any): any;
    onAddStep(...args: any): any;
    onRemoveStep(...args: any): any;
    onSourceConfigChanged(...args: any): any;
    onGetParameterizedURLs(...args: any): any;
    onValidateDataPath(...args: any): any;
    onAPIConfigChanged(...args: any): any;
    onProbeClearSample(...args: any): any;
    onProbeGetSample(...args: any): any;
    onProbeTemplatizedAPI(...args: any): any;
    validateConfigureRestSourceRequiredFields(...args: any): any;
    onSwitchUiMode(): void;
    goBack(): void;
    onSaveAdvancedMode(data: any): void;
    nextFormStage(...args: any): void;
    onError(msg: string): void;
  },
];

const RestAPIContext = React.createContext<ContextValue>(null);
export const useRestAPIContext = () => useContext<ContextValue>(RestAPIContext);
RestAPIContext.displayName = 'RestAPIRoot';

export function RestAPISourceProvider(props: Props) {
  const {
    formType,
    flowType,
    apiConfig,
    sourceConfig,
    selectedConnection,
    selectedCredential,
    onSourceConfigChanged,
  } = props;

  const [starting, setStarting] = useState(true);
  const [buildResponseByStage, setBuildResponseByStage] = useState(false);

  const history = useHistory();
  const { showError } = useRequests();
  const dispatch = useDispatch<any>();
  const currentOrg = useCurrentOrg();
  const { getProbeData } = useProbe();
  const { credentials } = useCredentials();
  const { getProbeSample } = useProbeAdapter();
  const meta = useGetMetaInfoByConnectorType(selectedConnection);
  const { data: formData, updateFormData } = useFlowsSetup(formType, flowType);
  const isLoadingProbeSample = useIsLoading(PROBE_GET_SAMPLE_REQUEST);
  const { isModal, onDismiss } = useFlowParams();
  const {
    paging,
    getAuthFields,
    getSourceConfigFields,
    getConnectionCategoryByConnectorType,
    getSourceConfigFieldsByConnection,
  } = usePaging();

  const { dataMaps, dataMapSamples, isLoadingDataMaps, fetchDataMaps, fetchDataMapSamples } =
    useDataMaps();

  const credID = selectedCredential?.id;
  const probe = getProbeData(credID);
  const fields = getSourceConfigFields(selectedConnection);
  const visibleFields = UIUtils.omitInvisibleFieldData(fields, sourceConfig);

  const {
    templatizedAPIConfig,
    apiResponseByStage,
    apiResponseUpdateTime,
    dataPathValidationResultByStage,
  } = formData;

  const { source } = formData;
  const isEditFlow = source?.id;

  const advancedFields = useMemo(
    () =>
      getSourceConfigFieldsByConnection(selectedConnection, filterFieldsBasedOnHCSourceField).map(
        (f: Field) => {
          if (f.name === 'hc_scale_factor') {
            const { options } = f;
            const config = sourceConfig?.['hc_scale_factor'] ?? {};
            return {
              ...f,
              options: UIUtils.disableInvalidHCOptions(options, config),
            };
          }
          return f;
        },
      ),
    [selectedConnection, sourceConfig],
  );

  const showExternalTrigger = source?.['adaptive_flow'] === true;

  /**
   * StartForm
   */
  useEffect(() => {
    let newSourceConfig = structuredClone(formData?.sourceConfig);

    const isTemplatizedAPI =
      getConnectionCategoryByConnectorType(selectedConnection) ===
      CONNECTION_CATEGORY_TEMPLATIZED_API;

    let newFormData: any = {
      selectedConnection,
      sourceConfig: newSourceConfig,
      credentialId: selectedCredential?.id,
      selectedCredential: selectedCredential,
    };

    let { apiConfig, templatizedAPIConfig } = RestAPIUtil.builders.buildApiConfigToRestAPI(
      isEditFlow,
      {
        source: source,
        dataMaps: null,
        fields: getSourceConfigFields(selectedConnection),
        apiConfig: { ...formData?.apiConfig },
        sourceConfig: newSourceConfig,
      },
    );

    const addedCred = formData?.addedCredentialId;
    const cred_id = isEditFlow
      ? (formData?.credentialId ?? newFormData?.credentialId)
      : (selectedCredential?.id ??
        (typeof addedCred === 'object' && addedCred?.id != null ? addedCred.id : addedCred));
    const credential = credentials[cred_id];

    const fields = getSourceConfigFields(selectedConnection) ?? [];
    newSourceConfig = RestAPIUtil.builders.buildSourceConfigToRestAPIFromDefaults(
      newSourceConfig,
      fields,
    );

    if (isTemplatizedAPI) {
      templatizedAPIConfig = RestAPIUtil.builders.buildTemplatizedAPIConfig({
        fields,
        templatizedAPIConfig,
        flowType,
      });
    }

    const { error, data: formDataFromCredential } = tryCatch(() =>
      getFormDataFromCredential({
        isEditFlow,
        apiConfig,
        sourceConfig: newSourceConfig,
        credentials_non_secure_data: credential?.credentials_non_secure_data,
      }),
    );

    if (error) console.error(error.message);

    const { templates, items } = getAuthFields(selectedConnection);
    const initialTemplate = templates?.length ? templates[0].id : null;

    newFormData = {
      apiConfig,
      ...newFormData,
      ...formDataFromCredential,
      templatizedAPIConfig,
      credentialId: cred_id,
      selectedCredential: credential,
      sourceConfig: newSourceConfig,
      apiResponseByStage: [],
      apiResponseUpdateTime: -1,
      selectedTemplate: initialTemplate,
      dataPathValidationResultByStage: {},
      connectionAuthFields: getActiveItems(templates, items, undefined),
    };

    updateFormData(newFormData);
    setTimeout(() => setStarting(false), 500);
  }, []);

  useEffect(() => {
    if (buildResponseByStage) {
      const stage = mapValues(probe.probeSample, (stageProbeSample, stageIndex) =>
        handleAPIResponseStage({
          stageIndex,
          stageProbeSample,
          onAPIConfigChanged,
        }),
      );

      setBuildResponseByStage(false);
      updateFormData({
        apiResponseByStage: stage,
        apiResponseUpdateTime: new Date(),
      });
    }
  }, [buildResponseByStage]);

  function onAddStep() {
    const newApiConfigToAddStep = RestAPIUtil.builders.buildNewApiConfigToAddStep(apiConfig);
    if (newApiConfigToAddStep.error) {
      console.error(newApiConfigToAddStep.message);
      return;
    }
    updateFormData({ apiConfig: newApiConfigToAddStep });
  }

  function onRemoveStep(stageIndex = -1) {
    const param = { apiConfig, stageIndex };
    const config = RestAPIUtil.builders.buildNewApiConfigToRemoveStep(param);
    if (config.error) {
      console.error(config.message);
      return;
    }
    updateFormData({ apiConfig: config });
    dispatch(probeRemoveRestStage(credID, stageIndex)); // adjust the stage indices that probe responses are stored against
  }

  function onGetParameterizedURLs(index = -1) {
    return RestAPIUtil.builders.buildParameterizedURLs({
      apiConfig,
      dataMaps,
      stageIndex: index,
      dataMapSamples,
      apiResponseByStage,
      dataPathValidationResultByStage,
    });
  }

  function onValidateDataPath(stageAPIConfig: any, stageIndex = 0) {
    updateFormData({
      ...RestAPIUtil.validators.validateDataPath({
        stageAPIConfig,
        stageIndex,
        apiResponseByStage,
        dataPathValidationResultByStage,
      }),
    });
  }

  const onAPIConfigChanged = (name: string, value: any, stageIndex = -1, data: any) => {
    const { metaInfoById } = paging;
    const newAPIConfig = Object.assign({}, apiConfig, data);
    let iterations = [...apiConfig['rest.iterations']];

    if (HC_SOURCE_FIELD_NAMES_AND_OTHERS.includes(name)) {
      newAPIConfig[name] = value;
      setVendorInformation(name, value, newAPIConfig, metaInfoById[selectedConnection]);
    } else if (stageIndex > -1 && stageIndex < iterations.length) {
      iterations[stageIndex] = { ...iterations[stageIndex], [name]: value };
    } else if (name === 'dataMapIds') {
      const nonDataMapIterations = getNonDataMapIterations(apiConfig);
      const dataMapIterations = map(value, (dataMapOption) => {
        /**
         * if dataMapSamples has dataMapOption.value => fetch data map samples
         */
        const mapId = dataMapOption.value;
        if (!has(dataMapSamples, mapId)) {
          fetchDataMaps();
          fetchDataMapSamples(mapId, 1, 5);
        }

        return RestAPIUtil.builders.buildIterationKey(mapId);
      });

      let dataMapKeys = {};
      if (dataMaps) {
        _.each(value, (m) => {
          let selectedDataMap = has(dataMaps, m.value) ? dataMaps[m.value] : null;

          let thisMapKeys = has(selectedDataMap, 'map_entry_schema.properties')
            ? _.keys(selectedDataMap.map_entry_schema.properties)
            : [];
          let keyName = getIterationKey(m.value);
          dataMapKeys[keyName] = thisMapKeys;
        });
      }

      iterations = [...nonDataMapIterations, ...dataMapIterations];
      newAPIConfig[name] = value;
      newAPIConfig['dataMapKeys'] = dataMapKeys;
    } else if (name !== 'start.cron' && name !== 'flow_triggers') {
      onAddStep();
      return;
    }

    newAPIConfig['rest.iterations'] = iterations;
    updateFormData({ apiConfig: newAPIConfig });
  };

  const onSwitchUiMode = () => {
    const apiConfigWithView = { ...apiConfig, 'ui.mode': UiMode.VIEW };
    updateFormData({ apiConfig: apiConfigWithView });
  };

  const onProbeClearSample = () => {
    const newApiResponseByStage = _.mapValues(apiResponseByStage, (value, key) =>
      startsWith(key, 'vendor_resource_data_')
        ? value
        : {
            apiResponseContent: '',
            apiResponseContentType: 'text/plain',
            apiResponseCode: null,
            apiResponseMessage: null,
          },
    );

    const newDataPathValidationResultByStage = {
      ...dataPathValidationResultByStage,
    };

    forEach(dataPathValidationResultByStage, (_value, key) => {
      if (!startsWith(key, 'vendor_resource_data_')) {
        delete newDataPathValidationResultByStage[key];
      }
    });

    updateFormData({
      apiResponseUpdateTime: new Date(),
      apiResponseByStage: newApiResponseByStage,
      dataPathValidationResultByStage: newDataPathValidationResultByStage,
    });

    const probeSample = getProbeData(credID);
    forEach(probeSample, (_, key) => {
      if (startsWith(key, 'vendor_resource_data_')) {
        dispatch(probeRemoveRestStage(credID, key));
      }
    });
  };

  const validateConfigureRestSourceRequiredFields = (stageIndex = -1) => {
    const stageAPIConfig = apiConfig['rest.iterations'][stageIndex];
    const { isPaginated } = stageAPIConfig;
    const sourceConfigFields = paging.sourceConfigById;
    const connectionFields = _.keyBy(sourceConfigFields[selectedConnection], (field) => field.name);
    return RestAPIUtil.validators.validateConfigureRestSourceRequiredFields({
      connectionFields,
      isPaginated,
      stageAPIConfig,
    });
  };

  const onProbeTemplatizedAPI = async () => {
    const validation = RestAPIUtil.validators.validateRequiredFieldsToRestAPI({
      data: templatizedAPIConfig,
      fields,
    });

    if (validation.error) {
      showError(validation.message);
      return;
    }

    const { error, data: sourceConfig } = tryCatch(() =>
      RestAPIUtil.builders.buildSourceConfigFromPropsToRestAPI({
        paging,
        apiConfig,
        selectedConnection,
        templatizedAPIConfig,
        currentOrg: currentOrg,
        connectionCategory: CONNECTION_CATEGORY_TEMPLATIZED_API,
      }),
    );

    if (error) console.warn(error.message);

    sourceConfig.resource_type = 'SOURCE';
    await getProbeSample(credID, 0, sourceConfig);
    setBuildResponseByStage(true);
  };

  /**
   * 1 - check if all required fields is set
   * 2 - check if the selected macro has value
   * 3 - build the new config and run probeGetSample
   */
  const onProbeGetSample = async (stageIndex = -1) => {
    const validation = validateConfigureRestSourceRequiredFields(stageIndex);
    if (validation.error) {
      showError(validation.message);
      return;
    }

    const parameterizedURLs = onGetParameterizedURLs(stageIndex); // substitute parameter values into parameterized URL
    if (parameterizedURLs) {
      const { urls } = parameterizedURLs;
      if (isEmpty(urls)) {
        showError('The selected macro has no values');
        return;
      }
    }

    const config = RestAPIUtil.builders.buildSourceConfigFromPropsToRestAPI({
      paging,
      apiConfig,
      currentOrg,
      stageIndex,
      selectedConnection,
    });
    await getProbeSample(credID, stageIndex, config);
    setBuildResponseByStage(true);
  };

  const goBack = useCallback(() => {
    if (isModal) {
      onDismiss?.({});
      return;
    }
    history.goBack();
  }, [isModal, onDismiss, history]);

  const state = {
    meta,
    paging,
    formType,
    flowType,
    apiConfig,
    sourceConfig,
    selectedConnection,
    selectedCredential,
    templatizedAPIConfig,
    isLoadingDataMaps,
    advancedFields,
    visibleFields,
    apiResponseByStage,
    apiResponseUpdateTime,
    isLoadingProbeSample,
    dataPathValidationResultByStage,
  };

  const actions = {
    showError,
    onAddStep,
    onRemoveStep,
    onSourceConfigChanged,
    onGetParameterizedURLs,
    onValidateDataPath,
    onAPIConfigChanged,
    onSwitchUiMode,
    onProbeClearSample,
    validateConfigureRestSourceRequiredFields,
    onProbeGetSample,
    onProbeTemplatizedAPI,
    goBack,
    nextFormStage: props?.nextFormStage,
    onSaveAdvancedMode: props?.onSaveAdvancedMode,
    onError: props?.onError,
  };

  const value = [state, actions];

  const ChildrenComponent: any =
    meta?.connectionCategory === CONNECTION_CATEGORY_TEMPLATIZED_API
      ? RestTemplatizedAPI
      : RestAPISource;

  if (starting) return <CenteredLoader />;

  return (
    <RestAPIContext.Provider value={value as any}>
      <ChildrenComponent showExternalTrigger={showExternalTrigger} />
    </RestAPIContext.Provider>
  );
}
